import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Loader2, Upload, Trash2, Eye, EyeOff, ChevronUp, ChevronDown, Images } from 'lucide-react';

import { API_BASE } from '../lib/apiBase';
const API = `${API_BASE}/api`;

const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
});

export default function LoginPageImagesPanel() {
    const [images, setImages] = useState([]);
    const [settings, setSettings] = useState({ slideshow_enabled: false, slideshow_interval_seconds: 6 });
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [imgsRes, setRes] = await Promise.all([
                axios.get(`${API}/admin/login-page-images`, { headers: authHeader() }),
                axios.get(`${API}/admin/login-page-settings`, { headers: authHeader() }),
            ]);
            setImages(imgsRes.data || []);
            setSettings(setRes.data || { slideshow_enabled: false, slideshow_interval_seconds: 6 });
        } catch (err) {
            toast.error('Failed to load login page settings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const handleUpload = async (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;
        setUploading(true);
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append('file', file);
                await axios.post(`${API}/admin/login-page-images`, fd, {
                    headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
                });
            }
            toast.success(`${files.length} image(s) uploaded`);
            await fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Upload failed');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const toggleEnabled = async (image) => {
        try {
            await axios.put(
                `${API}/admin/login-page-images/${image.id}`,
                { enabled: !image.enabled },
                { headers: authHeader() }
            );
            await fetchAll();
        } catch {
            toast.error('Update failed');
        }
    };

    const togglePage = async (image, page) => {
        const current = Array.isArray(image.pages) && image.pages.length > 0
            ? image.pages
            : ['login', 'register'];
        const next = current.includes(page)
            ? current.filter((p) => p !== page)
            : [...current, page];
        if (next.length === 0) {
            toast.error('Image must appear on at least one page');
            return;
        }
        try {
            await axios.put(
                `${API}/admin/login-page-images/${image.id}`,
                { pages: next },
                { headers: authHeader() }
            );
            await fetchAll();
        } catch {
            toast.error('Update failed');
        }
    };

    const reorder = async (image, direction) => {
        const sorted = [...images].sort((a, b) => a.display_order - b.display_order);
        const idx = sorted.findIndex((i) => i.id === image.id);
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= sorted.length) return;
        const a = sorted[idx];
        const b = sorted[swapIdx];
        try {
            await Promise.all([
                axios.put(`${API}/admin/login-page-images/${a.id}`, { display_order: b.display_order }, { headers: authHeader() }),
                axios.put(`${API}/admin/login-page-images/${b.id}`, { display_order: a.display_order }, { headers: authHeader() }),
            ]);
            await fetchAll();
        } catch {
            toast.error('Reorder failed');
        }
    };

    const handleDelete = async (image) => {
        if (!window.confirm(`Delete "${image.original_filename}"?`)) return;
        try {
            await axios.delete(`${API}/admin/login-page-images/${image.id}`, { headers: authHeader() });
            toast.success('Image deleted');
            await fetchAll();
        } catch {
            toast.error('Delete failed');
        }
    };

    const saveSettings = async (patch) => {
        const next = { ...settings, ...patch };
        setSettings(next);
        try {
            const res = await axios.put(`${API}/admin/login-page-settings`, next, { headers: authHeader() });
            setSettings(res.data);
            toast.success('Login page settings saved');
        } catch {
            toast.error('Failed to save settings');
        }
    };

    const sortedImages = [...images].sort((a, b) => a.display_order - b.display_order);
    const enabledCount = sortedImages.filter((i) => i.enabled).length;

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Images className="h-5 w-5 text-indigo-500" />
                        Login Page Images
                    </CardTitle>
                    <CardDescription>
                        Upload one or more images shown on the right side of the login screen. When more than one image is enabled and slideshow is on, they will rotate.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Slideshow settings */}
                    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
                        <div className="flex items-center justify-between">
                            <div>
                                <Label className="font-medium" htmlFor="slideshow-toggle">Slideshow mode</Label>
                                <p className="text-xs text-slate-500 mt-0.5">Rotate through enabled images on the login page</p>
                            </div>
                            <Switch
                                id="slideshow-toggle"
                                checked={settings.slideshow_enabled}
                                onCheckedChange={(v) => saveSettings({ slideshow_enabled: v })}
                                data-testid="login-slideshow-toggle"
                            />
                        </div>
                        {settings.slideshow_enabled && (
                            <div className="mt-3 flex items-center gap-3">
                                <Label htmlFor="slideshow-interval" className="text-sm text-slate-600">Interval (seconds)</Label>
                                <Input
                                    id="slideshow-interval"
                                    type="number"
                                    min={2}
                                    max={60}
                                    className="w-24"
                                    value={settings.slideshow_interval_seconds}
                                    onChange={(e) => setSettings({ ...settings, slideshow_interval_seconds: Number(e.target.value) })}
                                    onBlur={(e) => saveSettings({ slideshow_interval_seconds: Math.max(2, Math.min(60, Number(e.target.value) || 6)) })}
                                    data-testid="login-slideshow-interval"
                                />
                            </div>
                        )}
                    </div>

                    {/* Upload */}
                    <div className="flex items-center gap-3">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            multiple
                            onChange={handleUpload}
                            className="hidden"
                            data-testid="login-image-file-input"
                        />
                        <Button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            data-testid="login-image-upload-btn"
                        >
                            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                            {uploading ? 'Uploading…' : 'Upload images'}
                        </Button>
                        <p className="text-xs text-slate-500">JPEG, PNG or WEBP — max 8&nbsp;MB each. Multiple files allowed.</p>
                    </div>

                    {/* Image list */}
                    {loading ? (
                        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
                    ) : sortedImages.length === 0 ? (
                        <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
                            <Images className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                            <p className="text-sm text-slate-500">No images uploaded yet. The login page will show the default vinyl image.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <p className="text-xs text-slate-500">{enabledCount} of {sortedImages.length} image(s) enabled on the login page.</p>
                            {sortedImages.map((img, idx) => (
                                <div
                                    key={img.id}
                                    className={`flex items-center gap-3 p-2 rounded-lg border ${img.enabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'}`}
                                    data-testid={`login-image-row-${img.id}`}
                                >
                                    <div className="flex flex-col gap-0.5">
                                        <button
                                            type="button"
                                            onClick={() => reorder(img, 'up')}
                                            disabled={idx === 0}
                                            className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                                            data-testid={`login-image-up-${img.id}`}
                                        >
                                            <ChevronUp className="h-4 w-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => reorder(img, 'down')}
                                            disabled={idx === sortedImages.length - 1}
                                            className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                                            data-testid={`login-image-down-${img.id}`}
                                        >
                                            <ChevronDown className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <img
                                        src={`${API}/auth/login-page-images/${img.id}/raw`}
                                        alt={img.original_filename}
                                        className="h-16 w-28 object-cover rounded border border-slate-200"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-900 truncate">{img.original_filename}</p>
                                        <p className="text-xs text-slate-500">
                                            {(img.size / 1024).toFixed(0)} KB · {img.content_type}
                                        </p>
                                    </div>
                                    {/* Per-page assignment: pick which auth screen(s)
                                        this image appears on. Defaults to both. */}
                                    <div className="flex flex-col items-start gap-1 mr-2">
                                        {['login', 'register'].map((p) => {
                                            const isOn = (img.pages || ['login', 'register']).includes(p);
                                            const label = p === 'login' ? 'Sign in' : 'Create account';
                                            return (
                                                <label
                                                    key={p}
                                                    className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer select-none"
                                                    data-testid={`login-image-page-${p}-${img.id}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isOn}
                                                        onChange={() => togglePage(img, p)}
                                                        className="h-3.5 w-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                                    />
                                                    {label}
                                                </label>
                                            );
                                        })}
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => toggleEnabled(img)}
                                        title={img.enabled ? 'Disable' : 'Enable'}
                                        data-testid={`login-image-toggle-${img.id}`}
                                    >
                                        {img.enabled ? <Eye className="h-4 w-4 text-emerald-600" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDelete(img)}
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                        data-testid={`login-image-delete-${img.id}`}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
